import { Link, useParams } from '@tanstack/react-router'
import { AppShell, PageBody, initialsOf } from '@/components/AppShell'
import { EvidenceCard, orderedWordings } from '@/components/EvidenceCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { AxisAnswerSummary, BoardAnswerSummary } from './AnsweredSummary'
import { flatten, isAnswered, useAnswers, useInvite, useReport } from './useReview'

/**
 * Every axis with its evidence, in the order the printed copy runs.
 *
 * The page of scales that opens the PDF is deliberately not here (owner,
 * 2026-08-03): a reviewer is asked about the boards, and showing them where the
 * player already sits on an axis invites them to answer the number rather than
 * the position.
 */
export function ReportPage() {
  const { token } = useParams({ from: '/r/$token' })
  const invite = useInvite(token)
  const report = useReport(token)
  const answers = useAnswers(token)

  if (report.isLoading || !invite.data) {
    return (
      <AppShell>
        <PageBody className="flex flex-col gap-6">
          <Skeleton className="h-7 w-1/3" />
          <Skeleton className="h-72 w-full rounded-xl" />
          <Skeleton className="h-72 w-full rounded-xl" />
        </PageBody>
      </AppShell>
    )
  }

  const axes = report.data?.axes ?? []
  const flat = flatten(axes)

  return (
    <AppShell
      subtitle={`${invite.data.dataset.label} · ${invite.data.dataset.revision}`}
      identity={{
        initials: initialsOf(invite.data.nick),
        name: invite.data.displayName ?? invite.data.nick,
        detail: invite.data.title,
      }}
      right={
        <Button size="sm" asChild>
          <Link to="/r/$token/verify" params={{ token }}>
            Weryfikuj
          </Link>
        </Button>
      }
    >
      <PageBody className="flex flex-col gap-12">
        {axes.map((axis) => {
          const done = axis.boards.filter((board) =>
            isAnswered(answers.data?.boards[board.id]),
          ).length
          const axisAnswer = answers.data?.axes[axis.key]
          return (
            <section key={axis.key} className="flex flex-col gap-6">
              <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h2 className="font-serif text-lg">
                  Oś {axis.ordinal}: {axis.title}
                </h2>
                <span className="text-xs text-muted-foreground">
                  ocenione {done} z {axis.boards.length}
                </span>
              </header>
              {axisAnswer ? <AxisAnswerSummary answer={axisAnswer} /> : null}
              {axis.boards.map((board) => {
                const position =
                  flat.find((item) => item.board.id === board.id)?.index ?? 0
                // The badge says only that it was answered; what was said goes
                // in the panel below, in the reviewer's own words.
                const answer = answers.data?.boards[board.id]
                const rated = isAnswered(answer)
                return (
                  <EvidenceCard
                    key={board.id}
                    board={board}
                    axis={axis}
                    badge={rated ? <Badge tone="navy">oceniony</Badge> : null}
                  >
                    <div className="flex flex-col gap-3">
                      {answer ? (
                        <BoardAnswerSummary
                          answer={answer}
                          wordings={orderedWordings(board)}
                        />
                      ) : null}
                      <Button variant="outline" size="sm" className="self-start" asChild>
                        <Link
                          to="/r/$token/verify"
                          params={{ token }}
                          search={{ i: position }}
                        >
                          {rated ? 'Zmień ocenę' : 'Oceń dowód'}
                        </Link>
                      </Button>
                    </div>
                  </EvidenceCard>
                )
              })}
            </section>
          )
        })}
      </PageBody>
    </AppShell>
  )
}
